import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import MemoizedVideoPlayer from "../components/MemoizedVideoPlayer";
import FormattedDescription from "../components/FormattedDescription";
import { useDebounceValue } from "../hooks/useDebounceValue";
import { useSeriesEpisodes } from "../hooks/useSeriesEpisodes";
import { useTranslation } from "../hooks/useTranslation";
import { translatePreservingFrench } from "../utils/lang";
import { getEmbedVideoUrl } from "../utils/rokuUrl";

const MuniDetail = React.memo(() => {
  const { t, currentLanguage, translateText } = useTranslation();
  const [translatedMuni, setTranslatedMuni] = useState(null);
  const [translatedEpisodes, setTranslatedEpisodes] = useState([]);

  const { seriesId } = useParams();
  const {
    episodes,
    seriesData: muni,
    loading: contentLoading,
    error: contentError,
    isTranslating,
  } = useSeriesEpisodes(seriesId);

  const [error, setError] = useState(null);

  // Memoize these functions to prevent re-renders
  const getStoredVideoState = useCallback(() => {
    try {
      const storedState = sessionStorage.getItem(`video-state-${seriesId}`);
      return storedState ? JSON.parse(storedState) : null;
    } catch (e) {
      console.error("Error retrieving stored video state:", e);
      return null;
    }
  }, [seriesId]);

  const saveVideoState = useCallback(
    (url, title, episodeId) => {
      try {
        const state = { url, title, episodeId };
        sessionStorage.setItem(
          `video-state-${seriesId}`,
          JSON.stringify(state),
        );
      } catch (e) {
        console.error("Error saving video state:", e);
      }
    },
    [seriesId],
  );

  // Get initial state only once
  const initialVideoState = React.useMemo(
    () => getStoredVideoState(),
    [getStoredVideoState],
  );

  // Use refs for values that shouldn't trigger re-renders
  const videoUrlRef = useRef(initialVideoState?.url || null);
  const [videoUrl, setVideoUrl] = useState(initialVideoState?.url || null);
  const debouncedVideoUrl = useDebounceValue(videoUrl, 500);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(
    initialVideoState?.title || null,
  );
  const currentEpisodeIdRef = useRef(initialVideoState?.episodeId || null);
  const userHasSelectedVideo = useRef(!!initialVideoState);

  // Memoize handleEpisodeClick
  const handleEpisodeClick = useCallback(
    (episode) => {
      if (episode) {
        const url = getEmbedVideoUrl(episode.videoUrl);
        videoUrlRef.current = url;
        currentEpisodeIdRef.current = episode.id;
        setVideoUrl(url);
        setCurrentlyPlaying(episode.title);
        userHasSelectedVideo.current = true;
        saveVideoState(url, episode.title, episode.id);

        try {
          const seriesFirestoreId = muni?.firestoreId || muni?.id;
          const episodeFirestoreId = episode?.firestoreId || episode?.id;
          if (!seriesFirestoreId || !episodeFirestoreId) return;

          import("../utils/views")
            .then(({ incrementEpisodeView }) => {
              incrementEpisodeView(seriesFirestoreId, episodeFirestoreId).catch(
                () => {},
              );
            })
            .catch(() => {});
        } catch (_) {}
      }
    },
    [saveVideoState, muni],
  );

  // ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS

  // Translate muni and episodes when language changes
  useEffect(() => {
    const translateContent = async () => {
      if (!muni || currentLanguage === "en") {
        setTranslatedMuni(muni);
        setTranslatedEpisodes(episodes);
        return;
      }

      try {
        // Translate muni descriptions while preserving French content
        const translated = {
          ...muni,
          shortDescription: muni.shortDescription
            ? await translatePreservingFrench(
                muni.shortDescription,
                "fr",
                translateText,
              )
            : muni.shortDescription,
          longDescription: muni.longDescription
            ? await translatePreservingFrench(
                muni.longDescription,
                "fr",
                translateText,
              )
            : muni.longDescription,
        };
        setTranslatedMuni(translated);

        // Translate episodes
        const translatedEps = await Promise.all(
          episodes.map(async (ep) => ({
            ...ep,
            shortDescription: ep.shortDescription
              ? await translatePreservingFrench(
                  ep.shortDescription,
                  "fr",
                  translateText,
                )
              : ep.shortDescription,
            longDescription: ep.longDescription
              ? await translatePreservingFrench(
                  ep.longDescription,
                  "fr",
                  translateText,
                )
              : ep.longDescription,
          })),
        );
        setTranslatedEpisodes(translatedEps);
      } catch (error) {
        console.error("Translation error:", error);
        setTranslatedMuni(muni);
        setTranslatedEpisodes(episodes);
      }
    };

    translateContent();
  }, [muni, episodes, currentLanguage, translateText]);

  // Update video logic
  useEffect(() => {
    if (currentEpisodeIdRef.current && episodes.length > 0) {
      const currentEpisode = episodes.find(
        (ep) => ep.id === currentEpisodeIdRef.current,
      );
      if (currentEpisode) {
        const url = getEmbedVideoUrl(currentEpisode.videoUrl);
        if (url !== videoUrlRef.current) {
          videoUrlRef.current = url;
          setVideoUrl(url);
          setCurrentlyPlaying(currentEpisode.title);
        }
      }
    }
  }, [episodes]);

  // NOW CONDITIONAL RENDERING CAN HAPPEN

  // Use translatedMuni and translatedEpisodes instead of muni and episodes
  const displayMuni = translatedMuni || muni;
  const displayEpisodes =
    translatedEpisodes.length > 0 ? translatedEpisodes : episodes;

  // Only show the full loading screen on the initial fetch (no content yet).
  // Background re-translations triggered by onSnapshot updates should not
  // unmount the player – so we gate on whether muni data already exists.
  const isLoading = contentLoading && !muni;

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          {isTranslating ? t("loading") : t("loading")}
        </div>
      </div>
    );
  }

  // Update your error check to:
  if (error || contentError) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          Error: {error || contentError}
        </div>
      </div>
    );
  }

  // Render not found state
  if (!muni) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white font-bold text-lg md:text-xl p-4">
          No page found for title: {seriesId}
        </div>
      </div>
    );
  }

  // Render pay-per-view content
  if (muni && muni.tags && muni.tags.includes("ppv")) {
    return (
      <div className="flex items-center justify-center h-screen p-4 py-28">
        <iframe
          src={muni.embedLink}
          className="w-full h-full"
          title={muni.title}
          allowFullScreen
          frameBorder="0"
        ></iframe>
      </div>
    );
  }

  // Regular render
  return (
    <div className="flex flex-col lg:flex-row">
      <div className="w-full lg:w-1/2 h-full text-white py-10">
        <div className="p-4 py-10"></div>
        {currentlyPlaying && (
          <div className="pl-4">
            {t("currentlyPlaying")} {currentlyPlaying}
          </div>
        )}

        <div className="relative aspect-video" style={{ paddingTop: "56.25%" }}>
          {debouncedVideoUrl ? (
            <MemoizedVideoPlayer
              key={debouncedVideoUrl}
              videoUrl={debouncedVideoUrl}
            />
          ) : (
            displayMuni.thumbnail && (
              <img
                src={displayMuni.thumbnail}
                alt={displayMuni.title}
                className="absolute top-0 left-0 w-full h-full object-cover p-4"
              />
            )
          )}
        </div>

        <h2 className="text-white font-bold md:text-xl p-4 py-4">
          {displayMuni.title}
        </h2>
        {displayMuni.shortDescription && (
          <div className="pl-4 mt-2">
            <span className="font-semibold">{t("shortDescription")} </span>
            <FormattedDescription
              text={displayMuni.shortDescription}
              className="inline"
            />
          </div>
        )}
        {displayMuni.longDescription && (
          <div className="pl-4 mt-2">
            <span className="font-semibold">{t("longDescription")} </span>
            <FormattedDescription text={displayMuni.longDescription} />
          </div>
        )}
      </div>

      <div className="w-full lg:w-1/2 h-full text-white py-10">
        <div className="lg:py-10 px-10">
          <h3 className="font-bold text-lg md:text-xl">
            {t("videos")} ({displayEpisodes.length})
          </h3>
          <ul className="divide-y divide-gray-700 p-4">
            {displayEpisodes.length > 0 ? (
              displayEpisodes.map((episode) => {
                const isCurrentlyPlaying =
                  episode.id === currentEpisodeIdRef.current;

                return (
                  <li
                    key={`${episode.id}`}
                    className={`py-4 ${
                      isCurrentlyPlaying ? "bg-gray-900 rounded-md p-2" : ""
                    }`}
                  >
                    <button
                      className="w-full text-left"
                      onClick={() => handleEpisodeClick(episode)}
                    >
                      <div className="flex items-center gap-2">
                        {episode.isLive && (
                          <span className="bg-red-500 text-white px-2 py-1 rounded">
                            {t("live")}
                          </span>
                        )}
                        <p className="font-semibold">
                          {episode.title || "Untitled Episode"}
                        </p>
                      </div>
                    </button>
                    {episode.shortDescription && (
                      <div className="text-gray-400 mt-2">
                        <FormattedDescription text={episode.shortDescription} />
                      </div>
                    )}
                    {(episode.viewsTotal ?? 0) > 0 && (
                      <p className="text-gray-400 font-semibold mt-2">
                        {t("views")} {episode.viewsTotal}
                      </p>
                    )}
                    {episode.longDescription && (
                      <div className="text-gray-400 mt-2">
                        <FormattedDescription text={episode.longDescription} />
                      </div>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="py-4 text-center text-gray-500">
                {t("noVideosAvailable")}
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
});

MuniDetail.displayName = "MuniDetail";

export default MuniDetail;
